import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWithChat from './LayoutWithChat';
import Modal from './Modal';
import { 
  getPlatformStatuses, 
  initYahooAuth, 
  handleYahooCallback, 
  connectSleeper, 
  disconnectYahoo, 
  disconnectSleeper 
} from '../api/auth';

interface SettingsProps {
  onChangePlan?: () => void;
}

const Settings: React.FC<SettingsProps> = ({ onChangePlan }) => {
  const navigate = useNavigate();

  // Connection states
  const [yahooConnected, setYahooConnected] = useState<boolean | null>(null);
  const [sleeperConnected, setSleeperConnected] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Sleeper modal states
  const [showSleeperModal, setShowSleeperModal] = useState(false);
  const [sleeperUsername, setSleeperUsername] = useState('');

  // Check platform statuses on component mount
  useEffect(() => {
    checkPlatformStatuses();
    
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state && !isLoading) {
      // Clear the URL parameters immediately to prevent duplicate calls
      window.history.replaceState({}, document.title, window.location.pathname);
      handleOAuthCallback(code, state);
    }
  }, []);

  const checkPlatformStatuses = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const statuses = await getPlatformStatuses();
      setYahooConnected(statuses.yahoo.isOnboarded);
      setSleeperConnected(statuses.sleeper.isOnboarded);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to check connection status');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthCallback = async (code: string, state: string) => {
    setIsLoading(true);
    setError('');
    
    try {
      await handleYahooCallback(code, state);
      setYahooConnected(true);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to complete Yahoo authentication');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleYahooConnect = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Use empty string for localhost, otherwise use current URL
      const redirectUri = window.location.hostname === 'localhost' 
        ? 'https://derekslarsonrules.com/settings' 
        : window.location.origin + window.location.pathname;
      const response = await initYahooAuth(redirectUri);
      
      if (response.authUrl) {
        window.location.href = response.authUrl;
      }
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to connect to Yahoo Fantasy');
      }
      setIsLoading(false);
    }
  };

  const handleYahooDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Yahoo Fantasy account?')) {
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await disconnectYahoo();
      setYahooConnected(false);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to disconnect Yahoo account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSleeperConnect = () => {
    setShowSleeperModal(true);
    setError('');
  };

  const handleSleeperImport = async () => {
    if (!sleeperUsername.trim()) {
      setError('Please enter your Sleeper username');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await connectSleeper(sleeperUsername);
      setSleeperConnected(true);
      setShowSleeperModal(false);
      setSleeperUsername('');
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError(err.message || 'Failed to connect Sleeper account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSleeperDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Sleeper account?')) {
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await disconnectSleeper();
      setSleeperConnected(false);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to disconnect Sleeper account');
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <LayoutWithChat>
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-normal font-primary text-text-primary">
              Settings
            </h1>
            <p className="mt-2 text-lg text-text-secondary font-secondary">
              Manage your account settings and connections.
            </p>
          </div>
            {/* Manage Connections Section */}
            <div className="bg-bg-secondary border border-border-primary rounded-lg">
              <div className="p-6">
                <h2 className="text-2xl font-normal font-primary text-text-primary">
                  Manage Connections
                </h2>
                <p className="mt-2 text-base font-light text-text-secondary font-secondary">
                  Connect your fantasy football accounts to sync your leagues,
                  rosters, and scores.
                </p>
              </div>
              <div className="border-t border-border-primary p-6 space-y-4">
                {/* Yahoo Fantasy */}
                <div className="flex items-center justify-between p-4 bg-bg-tertiary/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img
                      alt="Yahoo Fantasy logo"
                      className="w-8 h-8"
                      src="https://s.yimg.com/cv/apiv2/default/icons/favicon_y19_32x32_custom.svg"
                    />
                    <div>
                      <h3 className="text-base font-medium text-text-primary font-primary">
                        Yahoo Fantasy
                      </h3>
                      <p className="text-sm font-light text-text-secondary font-secondary">
                        {yahooConnected === null 
                          ? 'Checking...' 
                          : yahooConnected 
                            ? 'Connected' 
                            : 'Not Connected'}
                      </p>
                    </div>
                  </div>
                  {yahooConnected === null ? (
                    <div className="w-20 h-8 bg-bg-tertiary animate-pulse rounded-lg"></div>
                  ) : yahooConnected ? (
                    <button
                      onClick={handleYahooDisconnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  ) : (
                    <button
                      onClick={handleYahooConnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>

                {/* Sleeper */}
                <div className="flex items-center justify-between p-4 bg-bg-tertiary/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img
                      alt="Sleeper app logo"
                      className="w-8 h-8 rounded-full"
                      src="https://cdn.brandfetch.io/idMCm9AmqM/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1747579480249"
                    />
                    <div>
                      <h3 className="text-base font-medium text-text-primary font-primary">
                        Sleeper
                      </h3>
                      <p className="text-sm font-light text-text-secondary font-secondary">
                        {sleeperConnected === null 
                          ? 'Checking...' 
                          : sleeperConnected 
                            ? 'Connected' 
                            : 'Not Connected'}
                      </p>
                    </div>
                  </div>
                  {sleeperConnected === null ? (
                    <div className="w-20 h-8 bg-bg-tertiary animate-pulse rounded-lg"></div>
                  ) : sleeperConnected ? (
                    <button
                      onClick={handleSleeperDisconnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  ) : (
                    <button
                      onClick={handleSleeperConnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Connect
                    </button>
                  )}
                </div>

                {/* Error Display */}
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Manage Plan Section */}
            <div className="bg-bg-secondary border border-border-primary rounded-lg">
              <div className="p-6">
                <h2 className="text-2xl font-normal font-primary text-text-primary">
                  Manage Plan
                </h2>
                <p className="mt-2 text-base font-light text-text-secondary font-secondary">
                  You are currently on the Pro Plan. You can upgrade or cancel
                  your subscription here.
                </p>
              </div>
              <div className="border-t border-border-primary p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-medium text-text-primary font-primary">
                    Pro Plan
                  </h3>
                  <p className="text-sm font-light text-text-secondary font-secondary">
                    $9.99 per month. Next payment on Oct 1, 2024.
                  </p>
                </div>
                <button
                  onClick={onChangePlan}
                  className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors whitespace-nowrap"
                >
                  Change Plan
                </button>
              </div>
            </div>
        </div>
      </LayoutWithChat>

      {/* Sleeper Username Modal */}
      <Modal 
        isOpen={showSleeperModal} 
        onClose={() => setShowSleeperModal(false)}
        title="Connect Sleeper"
      >
        <div className="space-y-4">
          <p className="text-text-secondary font-secondary">
            Enter your Sleeper username to import your leagues.
          </p>
          
          <input
            className="w-full bg-bg-tertiary border border-border-primary rounded-lg py-3 px-4 
                     focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent 
                     transition text-text-primary placeholder-text-secondary font-secondary"
            placeholder="Enter your Sleeper username"
            type="text"
            value={sleeperUsername}
            onChange={(e) => setSleeperUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSleeperImport();
              }
            }}
            autoFocus
          />
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowSleeperModal(false);
                setSleeperUsername('');
                setError('');
              }}
              className="text-text-secondary hover:text-text-primary font-medium font-secondary transition-colors px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={handleSleeperImport}
              disabled={!sleeperUsername.trim() || isLoading}
              className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {isLoading ? 'Connecting...' : 'Import Leagues'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Settings;